import type { ActionListValue } from '@latitude/action-list';
import { ActionList, DefaultOptionComponent } from '@latitude/action-list';
import { cn } from '@latitude/token-utils';
import { TriggerInput } from 'components/TriggerInput';
import { TriggerWrapper } from 'components/TriggerWrapper';

import { getTriggerInputClassNames } from './helpers';
import { useAsyncOptions } from './hooks/async/useAsyncOptions';
import { type AsyncSelectProps } from './types';

/**
 * Internal async Select — renders a single-select dropdown backed by
 * useAsyncOptions for remote fetching, debounced search, and pagination.
 * Not exported directly; consumers use Select with loadOptions.
 */
export const AsyncSelect = <isNullable extends boolean = false, T = undefined>({
  withSearch = true,
  isNullable,
  triggerClassName,
  inputClassName,
  itemSize = 'medium',
  value,
  onChange,
  trailingElement,
  placeholder,
  'aria-labelledby': ariaLabelledBy,
  loadOptions,
  disabled,
  searchPlaceholder = 'Search...',
  error,
  searchInputError,
  searchInputErrorMessage,
  ref,
  'data-testid': testID,
  readOnly,
  onBlur,
  rawProps,
  onOpenChange: passedOnOpenChange,
  OptionComponent = DefaultOptionComponent,
  ...props
}: AsyncSelectProps<isNullable, T>) => {
  const {
    open,
    setOpen,
    options,
    loading,
    inlineLoading,
    handleChange,
    handleDismiss,
    onSearchChange,
    onScroll,
    onOpenChange,
    handleTriggerKeyDown,
  } = useAsyncOptions<isNullable, T>({
    loadOptions,
    value: value as string | null | undefined,
    disabled,
    onBlur,
    onOpenChange: passedOnOpenChange,
    onChange,
  });

  return (
    <ActionList<false, isNullable, T>
      {...props}
      open={!readOnly && open}
      setOpen={setOpen}
      options={options}
      withSearch={withSearch}
      onChange={handleChange}
      placeholder={searchPlaceholder}
      value={value as ActionListValue<false, isNullable>}
      disabled={disabled}
      onOpenChange={onOpenChange}
      itemSize="small"
      data-testid={testID}
      isExternalSearch
      onSearchChange={onSearchChange}
      loading={loading}
      inlineLoading={inlineLoading}
      OptionComponent={OptionComponent}
      onScroll={onScroll}
      error={error || searchInputError}
      errorMessage={searchInputErrorMessage}
      onDismiss={handleDismiss}
      rawProps={rawProps}
    >
      <TriggerWrapper
        aria-disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn(triggerClassName, 'group')}
        tabIndex={disabled || readOnly ? -1 : 0}
        onKeyDown={handleTriggerKeyDown}
      >
        <TriggerInput
          disabled={disabled}
          inputSize={itemSize}
          classNames={getTriggerInputClassNames(disabled, inputClassName)}
          ref={ref}
          trailingElement={trailingElement}
          clearable={false}
          readOnly
          debounce={0}
          direction={open ? 'up' : 'down'}
          isMulti={false}
          isNullable={isNullable}
          data-testid={testID ? `${testID}-input` : undefined}
          placeholder={placeholder}
          aria-labelledby={ariaLabelledBy}
          error={error}
        />
      </TriggerWrapper>
    </ActionList>
  );
};

AsyncSelect.displayName = 'AsyncSelect';
